
import { defineComponent, onMounted, reactive, ref } from "vue";
import { useSidClient } from "@/plugins/auth";
import { useRoute, useRouter } from "vue-router";
import { PASSWORD_MAX_LENGTH } from "@/commons/constants";
import useDeterring from "@/commons/useDeterring";
import { QuestionnaireItem } from "@/modules/questionnaire/questionnaireInfo";
import { isSiteRegistered, toBoolean } from "@/commons/commonUtility";
import { loginStatus } from "@/modules/loginStatus";
import { ALL_LINKAGE_ID, isQualified, getLinkIdFromSiteCode, getMailMagazineName, SITE_CONFIGS } from "@/commons/questionnaire/questionnaire";
import { QuestionnaireUserItem } from "@/modules/questionnaire/questionnaireUserItem";
import { store } from "@/store";

export default defineComponent({
    name: "QuestionnaireLogin",
    props: {
        // ログインエラー
        loginErrorMsg: { type: String, default: "" },
        // サービス未登録エラー
        isServiceNotRegError: { type: String, default: "false" },
        // メルマガ未登録エラー
        isMmNotRegError: { type: String, default: "false" },
    },
    setup(props) {
        const sidClient = useSidClient();
        const router = useRouter();
        const { isDeterringRef, setNotRelease, asyncDeterringWrap } = useDeterring();
        const { questionnaireInfo, fetchToken, routeNames } = QuestionnaireItem.inject();
        const userBaseItem = QuestionnaireUserItem.inject();

        /* Reactive */
        const emailRef = ref("");
        const passwordRef = ref("");
        const disableAutoLoginRef = ref(false);
        const autoLoginRef = ref(true);
        const loginErrorMsgRef = ref("");
        const isServiceNotRegErrorRef = ref(false);
        const isMmNotRegErrorRef = ref(false);

        const passwordToggle = reactive<{
            fieldType: string;
            isOn: boolean;
        }>({
            fieldType: "password",
            isOn: false
        });

        // アンケート対象のサイト情報を取得
        const linkId = getLinkIdFromSiteCode(questionnaireInfo.siteCode);
        const siteInfo = store.state.siteLinkage.find(site => site.link_id === linkId);
        const serviceName = siteInfo?.name as string;
        const mailMagazineName = getMailMagazineName(linkId, questionnaireInfo.mmExtId);
        const isApl = siteInfo?.kind === "apl";
        const isReal = siteInfo?.kind === "real";
        const siteConfig = SITE_CONFIGS.find(config => config.linkId === linkId)!.config;

        // サービス遷移先URL
        let serviceRegUrl: string;
        let serviceLoginUrl: string;
        let serviceUpdateAttrUrl: string;
        if (linkId === ALL_LINKAGE_ID) {
            // 全SID会員対象アンケートの場合はSIDポータル画面
            serviceRegUrl = "/signup";
            serviceLoginUrl = "/login";
            serviceUpdateAttrUrl = "/mypage/top";
        } else if (siteInfo?.kind === "apl" || siteInfo?.kind === "real") {
            // サービスの種別が'アプリ'または'実店舗'の場合はSIDポータル画面(暫定的)
            serviceRegUrl = "/signup";
            serviceLoginUrl = "/login";
            serviceUpdateAttrUrl = "/mypage/top";
        } else if (siteConfig.isDirectMove) {
            // 直接遷移対象サイトの場合
            serviceRegUrl = `${window.location.origin}${siteConfig.registerUrl!}`;
            serviceLoginUrl = `${window.location.origin}${siteConfig.loginUrl!}`;
            serviceUpdateAttrUrl = `${window.location.origin}${siteConfig.updateAttrUrl!}`;
        } else {
            // 上記以外
            serviceRegUrl = siteInfo?.register_url as string;
            serviceLoginUrl = siteInfo?.login_url as string;
            serviceUpdateAttrUrl = siteInfo?.login_url as string;
        }

        /**
         * エラーメッセージクリア
         */
        const clearErrorMsg = () => {
            loginErrorMsgRef.value = "";
            isServiceNotRegErrorRef.value = false;
            isMmNotRegErrorRef.value = false;
        };

        /**
         * パスワード表示・非表示切替
         * @param toggle { fieldType: string; isOn: boolean }
         */
        const switchPasswordFieldType = (toggle: { fieldType: string; isOn: boolean }) => {
            if (toggle.fieldType === "password") {
                toggle.fieldType = "text";
                toggle.isOn = true;
            } else {
                toggle.fieldType = "password";
                toggle.isOn = false;
            }
            return toggle;
        };

        /**
         * サービス連携サイトへ遷移
         * @param url {string} サービス連携サイトURL
         */
        const goToLinkageSite = (url: string, keepLogin = false) => {
            if (!siteConfig.isDirectMove) {
                // 直接遷移できないサイトの場合は新規タブで遷移
                window.open(url, "_blank");
            } else {
                // 直接遷移してこの画面に戻るようにコールバック指定する（ログイン状態も維持）
                const urlObj = new URL(url);
                urlObj.searchParams.set("callback", window.location.href);
                if (keepLogin) {
                    urlObj.searchParams.set("email", emailRef.value);
                    urlObj.searchParams.set("keepLogin", sidClient.clientConfig.authInfo.clientId);
                }
                window.location.href = urlObj.toString();
            }
        };

        /**
         * ログイン
         * @async
         */
        const login = async () => {
            try {
                clearErrorMsg();

                // 念の為サインアウト
                await sidClient.signOut();
                loginStatus.value = false;

                // セッションストレージ削除
                userBaseItem.removeStorage();

                // 自動ログイン無効化判断
                if (disableAutoLoginRef.value) {
                    autoLoginRef.value = false;
                }

                // サインイン
                let userName;
                try {
                    const result = await sidClient.signIn(emailRef.value, passwordRef.value, autoLoginRef.value);
                    if (result === "NEW_PASSWORD_REQUIRED") {
                        // 管理画面でアカウント登録された場合、または、管理画面でパスワード変更を行った場合
                        await router.push({
                            name: routeNames.NEW_PASSWORD
                        });
                        return;
                    } else if (result === "RESET_PASSWORD_REQUIRED") {
                        // Cognitoでパスワード変更された場合
                        await router.push({
                            name: routeNames.SET_PASSWORD,
                            // eslint-disable-next-line
                            query: {reset_email: emailRef.value}
                        });
                        return;
                    }
                    userName = (await sidClient.getUserData()).Username;
                } catch (e) {
                    if (e.code == "UserNotConfirmedException") {
                        // 実店舗ユーザ（ユーザ名でログイン）の場合のみだが、
                        // 実店舗ユーザは必ず確認済みのステータスとなる仕様のため、デッドコード
                        loginErrorMsgRef.value = "ログインできませんでした。";
                    } else {
                        loginErrorMsgRef.value = "ログインできませんでした。メールアドレスとパスワードをご確認ください。";
                    }
                    return;
                }

                // 基本属性情報取得
                await userBaseItem.fetchUserData(sidClient);

                // ユーザ名格納
                userBaseItem.userBase.userName = userName;

                // セッションストレージ格納
                userBaseItem.storeStorage();

                // サイト登録有無チェック
                if (linkId !== ALL_LINKAGE_ID && !isSiteRegistered(linkId, userBaseItem.userBase)) {
                    // エラーメッセージ返却
                    isServiceNotRegErrorRef.value = true;
                    return;
                }

                // メルマガ購読に関して条件があるか？
                if (questionnaireInfo.mmFlg || questionnaireInfo.mmExtId) {
                    // メルマガ購読に関する条件に該当するか？
                    if (!await isQualified(linkId, questionnaireInfo)) {
                        isMmNotRegErrorRef.value = true;
                        return;
                    }
                }

                // トークンをコールバックする
                await fetchToken(sidClient);
                setNotRelease();
                window.location.href = questionnaireInfo.callbackUrlWithToken as string;

            } catch (e) {
                await router.push({ name: routeNames.GENERAL_ERROR, params: { message: "お手数をおかけしますが、しばらく時間を置いてから、再度操作していただけますようお願いいたします。" } });
            }
        };

        onMounted((): void => { 
            loginErrorMsgRef.value = props.loginErrorMsg as string;
            isServiceNotRegErrorRef.value = toBoolean(props.isServiceNotRegError);
            isMmNotRegErrorRef.value = toBoolean(props.isMmNotRegError);
        });

        return {
            isDeterringRef,
            asyncDeterringWrap,
            emailRef,
            passwordRef,
            disableAutoLoginRef,
            serviceRegUrl,
            serviceLoginUrl,
            serviceUpdateAttrUrl,
            loginErrorMsgRef,
            isServiceNotRegErrorRef,
            isMmNotRegErrorRef,
            serviceName,
            mailMagazineName,
            isApl,
            isReal,
            PASSWORD_MAX_LENGTH,
            passwordToggle,
            switchPasswordFieldType,
            goToLinkageSite,
            login,
            routeNames,
        };
    },
});
